import { useState } from 'react'
import clsx from 'clsx'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { AlertErrorCircle, FilePdf } from '@/assets/icons'
import { InlineMessage } from '@/components/inline-message/InlineMessage'

/** An update that finalized with some files unprocessed, pending an owner's acknowledgement. */
export type MatterRecordFailedFiles = {
  updateId: string
  /** Display names of the files that weren't processed. */
  displayNames: string[]
}

type MatterRecordBannersProps = {
  /** Hides the owner-only actions on the failed-files and error banners. */
  isReadOnly: boolean
  /** An update is running — shows the in-progress strip. */
  isUpdateActive: boolean
  /** Feature-formatted progress copy for the in-progress strip, e.g. "Extracting… 2 of 5 files". */
  activeStatusLabel: string
  /** Unresolved partial failure, or null. */
  failedFiles: MatterRecordFailedFiles | null
  /** Id of the errored update to offer dismissing, or null when the last update didn't error. */
  erroredUpdateId: string | null
  /** Disables "Dismiss" on the failed-files banner while a mutation is in flight. */
  mutationPending: boolean
  /** Inline mutation error copy, or null. */
  mutationError: string | null
  /** Finalizes the update excluding the failed files (the owner-only "Dismiss"). */
  onCompleteExcludingFailed: (updateId: string) => void
  /** Opens the version-history panel — wired to the failed-files banner's "Version info" action. */
  onShowVersionInfo?: () => void
  isTabHeaderMultiRow?: boolean
}

/**
 * Status banners for a living matter asset, rendered as full-width strips across the top of the
 * content area (Figma "Error Handling" treatment): in-progress update strip, unresolved FAILED_FILES
 * decision strip, failed-update notice, and inline mutation errors. At most one update banner shows,
 * in that order of precedence.
 *
 * Presentational: the feature selects the state and supplies the progress copy and the resolve
 * action. Dismissing the error banner is local and lasts for the session, keyed by update id.
 */
export default function MatterRecordBanners(props: MatterRecordBannersProps) {
  const {
    isReadOnly,
    isUpdateActive,
    activeStatusLabel,
    failedFiles,
    erroredUpdateId,
    mutationPending,
    mutationError,
    onCompleteExcludingFailed,
    onShowVersionInfo,
    isTabHeaderMultiRow = false,
  } = props

  // "Dismiss" on the error banner hides it for this session (keyed by update id).
  const [dismissedUpdateId, setDismissedUpdateId] = useState<string | null>(null)

  const banners: React.ReactNode[] = []

  if (isUpdateActive) {
    banners.push(
      <div key="updating" className="flex items-center gap-1 pl-4 pr-2 py-2.5 bg-white border border-brand-neutral-200 rounded-md">
        <CircularProgressContinuousSized size={24} thickness={4} />
        <span className="text-xs font-medium text-brand-neutral-950">{activeStatusLabel}</span>
      </div>
    )
  } else if (failedFiles) {
    // Figma "toolbar/version" partial-error (9093:363386): a dismissable notice (the update already
    // finalized with the successful files; failed files are retryable from the Source Files tab).
    banners.push(
      <div key="failed-files" className="flex flex-col overflow-hidden rounded-md border border-brand-neutral-200 bg-yellow-50">
        <div className="flex flex-col sm:flex-row items-start md:items-center gap-2.5 pl-4 pr-2 py-3">
          {/* Stacks below sm so the explainer keeps full width instead of wrapping in a sliver
              beside the nowrap headline. */}
          <div className="flex-1 min-w-0 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
            <span className="flex items-center gap-1 shrink-0">
              <AlertErrorCircle className="h-3.5 w-3.5 shrink-0 text-yellow-800" aria-hidden />
              <span className="text-xs font-medium text-yellow-800 whitespace-nowrap">Some files couldn&rsquo;t be added.</span>
            </span>
            <span className="text-xs font-medium text-brand-neutral-950">
              The files below weren&rsquo;t processed &mdash; all other events were added successfully.
            </span>
          </div>
          {/* Collaborators can't resolve or retry a failed run, so these actions are owner-only. */}
          {!isReadOnly && (
            <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto">
              <button
                onClick={() => onShowVersionInfo?.()}
                className="px-2 py-0.5 rounded-[4px] text-xs font-medium text-brand-neutral-950 hover:bg-yellow-100"
              >
                Version info
              </button>
              <button
                onClick={() => onCompleteExcludingFailed(failedFiles.updateId)}
                disabled={mutationPending}
                className="px-2 py-0.5 rounded-[4px] text-xs font-medium text-brand-neutral-950 hover:bg-yellow-100 disabled:opacity-60"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
        {failedFiles.displayNames.length > 0 && (
          <div className="flex flex-col border-t border-brand-neutral-200 bg-white py-1">
            {failedFiles.displayNames.map((name, index) => (
              <div key={`${name}-${index}`} className="flex h-9 items-center gap-2 px-4">
                <FilePdf className="w-5 h-5 shrink-0 text-brand-neutral-800" />
                <span className="min-w-0 break-words text-sm tracking-[-0.15px] text-brand-neutral-950">{name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  } else if (erroredUpdateId && erroredUpdateId !== dismissedUpdateId) {
    // Figma "toolbar/version" error (9091:361433): red strip, single "Dismiss" — no Error ID, no Try again.
    banners.push(
      <div
        key="error"
        className="flex flex-col sm:flex-row items-start md:items-center gap-2.5 pl-4 pr-2 py-2.5 bg-red-50 border border-brand-neutral-200 rounded-md"
      >
        <div className="flex-1 min-w-0 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
          <span className="flex items-center gap-1 shrink-0">
            <AlertErrorCircle className="h-3.5 w-3.5 shrink-0 text-red-800" aria-hidden />
            <span className="text-xs font-medium text-red-800 whitespace-nowrap">Files couldn&rsquo;t be added.</span>
          </span>
          <span className="text-xs font-normal text-brand-neutral-950">
            The existing events and sources weren&rsquo;t affected{isReadOnly ? '.' : ' - please try again.'}
          </span>
        </div>
        <button
          onClick={() => setDismissedUpdateId(erroredUpdateId)}
          className="self-end sm:self-auto shrink-0 px-2 py-0.5 rounded-[4px] text-xs font-medium text-brand-neutral-950 hover:bg-red-100"
        >
          Dismiss
        </button>
      </div>
    )
  }

  if (mutationError) {
    banners.push(
      <InlineMessage key="mutation-error" variant="error">
        {mutationError}
      </InlineMessage>
    )
  }

  if (banners.length === 0) return null

  // Keep the left edge aligned with the first tab in either tab layout.
  // The right edge retains its timestamp/action-row alignment.
  return <div className={clsx('flex flex-col gap-2 mb-2 mr-2', isTabHeaderMultiRow ? 'ml-2' : 'ml-1 lg:ml-0')}>{banners}</div>
}
